import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './download.css';
import emailjs from 'emailjs-com';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../FirebaseComp/firebase';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useEffect } from "react";
import { motion, AnimatePresence } from 'framer-motion';

function Payment() {
  const [user, setUser] = useState(null);
  const [activeForm, setActiveForm] = useState('registration'); 
  const [formData, setFormData] = useState({ 
    email: '',
    name: '',
    contactNo: '',
    nicPassportNo: '',
    additional: '',
    paymentSlipFile: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const stateName = name === 'application' ? 'applicationFile' : 'paymentSlipFile';
    
    setFormData((prevData) => ({
      ...prevData,
      [stateName]: files[0],
    }));
  };

  const validateFiles = () => {
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    
    // Only validate application file for exam fee
    if (activeForm === 'exam' && !formData.applicationFile) {
      return 'Please upload the exam application file';
    }
    
    if (!formData.paymentSlipFile) {
      return 'Please upload the payment slip';
    }
    
    if (activeForm === 'exam' && formData.applicationFile && formData.applicationFile.size > MAX_SIZE) {
      return 'Application file is too large (max 5MB)';
    }
    
    if (formData.paymentSlipFile.size > MAX_SIZE) {
      return 'Payment slip is too large (max 5MB)';
    }
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    const validationError = validateFiles();
    if (validationError) {
      setSubmitError(validationError);
      setIsSubmitting(false);
      return;
    }

    try {
      const storage = getStorage();
      
      // Upload payment slip
      const paymentFileName = `payment_${Date.now()}_${formData.paymentSlipFile.name}`;
      const paymentRef = ref(storage, `formSubmissions/${paymentFileName}`);
      await uploadBytes(paymentRef, formData.paymentSlipFile);
      const paymentSlipUrl = await getDownloadURL(paymentRef);

      // Only upload application file if it exists (mainly for exam fee)
      let applicationFileUrl = '';
      let appFileName = '';
      if (formData.applicationFile) {
        appFileName = `application_${Date.now()}_${formData.applicationFile.name}`;
        const applicationRef = ref(storage, `formSubmissions/${appFileName}`);
        await uploadBytes(applicationRef, formData.applicationFile);
        applicationFileUrl = await getDownloadURL(applicationRef);
      }

      // Save text data to Firestore
      const submissionData = {
        name: formData.name,
        email: formData.email,
        contactNo: formData.contactNo,
        nicPassportNo: formData.nicPassportNo,
        additional: formData.additional,
        paymentFileName: paymentFileName,
        paymentSlipUrl,
        submittedAt: serverTimestamp(),
        status: 'pending',
        feeType: activeForm
      };

      // Only include application file data if it exists
      if (formData.applicationFile) {
        submissionData.applicationFileName = appFileName;
        submissionData.applicationFileUrl = applicationFileUrl;
      }

      await addDoc(collection(db, 'formSubmissions'), submissionData);

      // Prepare email data
      const emailData = {
        name: formData.name,
        email: formData.email,
        contactNo: formData.contactNo,
        nicPassportNo: formData.nicPassportNo,
        additional: formData.additional,
        paymentSlipUrl,
        feeType: activeForm
      };

      // Include application file in email if it exists
      if (formData.applicationFile) {
        emailData.applicationFileUrl = applicationFileUrl;
        emailData.applicationFileName = appFileName;
      }

      await emailjs.send(
        'service_84j0r2n',
        'template_acay6w6',
        emailData,
        'cUTURbs_jyRXHcEJv'
      );
  
      setSubmitSuccess(true);
      setFormData({
        email: '',
        name: '',
        contactNo: '',
        nicPassportNo: '',
        additional: '',
        applicationFile: null,
        paymentSlipFile: null,
      });
      
    } catch (err) {
      console.error('Failed to submit registration:', err);
      setSubmitError(`Failed to submit registration: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Form variants for animation
  const formVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 }
  };

  // Button variants for animation
  const buttonVariants = {
    active: { backgroundColor: "#4CAF50", color: "white" },
    inactive: { backgroundColor: "#f5f5f5", color: "#333" }
  };

  return (
    <div className='application-download'>

      <h1 className="student-panel-title">Payment Portal</h1>
      <p className="student-guidlines-description">
        Please select the type of payment you wish to submit. Choose between Registration Fee, Membership, 
        Event Fee, or Sponsorship and fill out the corresponding form with your details and payment slip.
      </p>
       
      {/* Fee Type Selection Buttons */}
      <div className="fee-type-buttons">
        <motion.button
          variants={buttonVariants}
          animate={activeForm === 'registration' ? "active" : "inactive"} 
          onClick={() => setActiveForm('registration')}
          className="fee-button"
        >
          Registration Fee
        </motion.button>
        


        <motion.button
          variants={buttonVariants}
          animate={activeForm === 'membership' ? "active" : "inactive"}
          onClick={() => setActiveForm('membership')}
          className="fee-button"
        >
          Membership
        </motion.button>



        <motion.button
          variants={buttonVariants}
          animate={activeForm === 'event' ? "active" : "inactive"}
          onClick={() => setActiveForm('event')}
          className="fee-button"
        >
          Event Fee
        </motion.button>
        
        <motion.button
          variants={buttonVariants}
          animate={activeForm === 'sponsorship' ? "active" : "inactive"}
          onClick={() => setActiveForm('sponsorship')}
          className="fee-button"
        >
          Sponsorship 
        </motion.button>
      </div>

      {user ? (
        submitSuccess ? (
          <div className="success-message">
            <h3 className='download-heading-form'>Thank you for your submission!</h3>
            <p>Your {activeForm} payment has been received successfully. We will review it and get back to you soon.</p>
            {/* <button 
              className="submit-button"
              onClick={() => setSubmitSuccess(false)}
            >
              Submit Another Payment
            </button> */}
          </div>

        ) : (

          <AnimatePresence mode="wait">
            <motion.div
              key={activeForm}
              variants={formVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="form-container"
            >
              <form className="submission-form" onSubmit={handleSubmit}>
                <h2 className="form-title">
                  {activeForm === 'registration' && 'Registration Fee Payment'}
                  {activeForm === 'membership' && 'Membership Fee Payment'}
                  {activeForm === 'sponsorship' && 'Sponsorship Fee Payment'}
                  {activeForm === 'event' && 'Event Fee Payment'}
                </h2>
                
                <div className="form-group">
                  <label htmlFor="fullName">Full Name *</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    placeholder="Enter your full name" 
                    value={formData.name}
                    onChange={handleChange}
                    required 
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    placeholder="example@email.com" 
                    value={formData.email}
                    onChange={handleChange}
                    required 
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contactNo">Contact Number *</label>
                  <input 
                    type="tel" 
                    id="contactNo" 
                    name="contactNo" 
                    placeholder="+94 77 123 4567" 
                    value={formData.contactNo}
                    onChange={handleChange}
                    required 
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="nicPassportNo">NIC Number *</label>
                  <input 
                    type="text" 
                    id="nicPassportNo" 
                    name="nicPassportNo" 
                    placeholder="199012345678 or 901234567V" 
                    value={formData.nicPassportNo}
                    onChange={handleChange}
                    required 
                  />
                </div>

                {/* Application file upload - only required for exam fee */}
                {/* {activeForm === 'exam' && (
                  <div className="form-group file-upload">
                    <label htmlFor="application">Upload E (PDF) *</label>


                    {/* Custom-styled upload area 

                    <div style={{ position: 'relative', margin: '20px 0' }}>
                      <input 
                        type="file" 
                        id="application" 
                        name="application"
                        style={{
                          position: 'relative',
                          display: 'block',
                          width: '100%',
                          padding: '15px',
                          border: '2px solid blue',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          zIndex: 1000,
                          opacity: 1,
                          visibility: 'visible'
                        }}
                        accept=".pdf,.doc,.docx" 
                        onChange={handleFileChange}
                        required 
                      />
                    </div>

                    {formData.applicationFile && (
                      <div className="file-info">
                        Selected: {formData.applicationFile.name} ({Math.round(formData.applicationFile.size / 1024)} KB)
                      </div>
                    )}
                    <small>Max file size: 5MB</small>
                  </div>
                )} */}



                {/* Payment slip upload - always required */}
                <div className="form-group file-upload">
                  <label htmlFor="paymentSlip">
                    {activeForm === 'registration' && 'Upload Registration Fee Payment Slip *'}
                     {activeForm === 'membership' && 'Upload Membership Fee Payment Slip *'}
                    {activeForm === 'event' && 'Upload Event Fee Payment Slip *'}
                    {activeForm === 'sponsorship' && 'Upload Sponsorship Payment Slip *'}
                  </label>

<div style={{ position: 'relative', margin: '20px 0' }}>
  <input 
    type="file" 
    id="paymentSlip" 
    name="paymentSlip"
    style={{
      position: 'relative',
      display: 'block',
      width: '100%',
      padding: '15px',
      border: '2px solid blue',
      borderRadius: '4px',
      backgroundColor: 'white',
      zIndex: 1000,
      opacity: 1,
      visibility: 'visible'
    }}
    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
    onChange={handleFileChange}
    required 
  />
</div>


                  {formData.paymentSlipFile && (
                    <div className="file-info">
                      Selected: {formData.paymentSlipFile.name} ({Math.round(formData.paymentSlipFile.size / 1024)} KB)
                    </div>
                  )}
                  <small>Max file size: 5MB</small>
                </div>

                <div className="form-group">
                  <label htmlFor="additional">Additional Comments</label>
                  <textarea 
                    id="additional" 
                    name="additional" 
                    placeholder="Any special instructions or notes..." 
                    value={formData.additional}
                    onChange={handleChange}
                    rows="4"
                  ></textarea>
                </div>

                {submitError && <div className="error-message">{submitError}</div>}

                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : `Submit ${activeForm.charAt(0).toUpperCase() + activeForm.slice(1)} Payment`}
                </button>
              </form>
            </motion.div>
          </AnimatePresence>
        )
      ) : (
        <div className="login-required-message">
          <h3>Please log in to submit your payment</h3>
          <p>
            You need to <Link to="/signin">sign in</Link> before submitting your payment. 
            If you don't have an account, please <Link to="/signup">sign up</Link>.
          </p>
        </div>
      )}
    </div>
  );
}

export default Payment;
